import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack,
  Box,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  Form, RecordContextProvider, useNotify, useTranslate, useRefresh,
  required, Confirm,
} from 'react-admin';
import _ from 'lodash';
import {
  Asset, PointGeometry, SecurityInventoryCategory, SecurityInventoryItem,
} from '@x-guard/xgac-types/xgac';
import { getMinimumNextDate } from '../../../../../utils/timeGranularityUtils';
import { GrayTextInputWithUpperLabel } from '../../../components/inputs/GrayTextInputWithUpperLabel';
import { GrayGoogleCoordinatesInputWithUpperLabel } from '../../../components/inputs/GrayGoogleCoordinatesInputWithUpperLabel';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { securityItemsResource } from '../util';
import { GrayNumberInputWithUpperLabel } from '../../../components/inputs/GrayNumberInputWithUpperLabel';
import { ExpiryDateDisplay } from '../../../components/ExpiryDateDisplay';
import { GrayDateInputWithUpperLabel } from '../../../components/inputs/GrayDateInputWithUpperLabel';
import { NextEmailAlert } from '../../../components/NextEmailAlert';

export const EditItemModal = (props: {
  record: SecurityInventoryItem | null;
  categories: SecurityInventoryCategory[];
  users: Asset[];
  open: boolean;
  onClose: () => void;
}) => {

  const record = props.record;

  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const category = useMemo(() => {

    return props.categories.find((c) => c._id === record?.category?._id) ?? null;

  }, [props.categories, record]);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<Record<string, unknown> | null>(null);

  if (!record) {

    return null;

  }

  const performUpdate = async (
    values: Record<string, unknown>,
    moveMarker: boolean,
  ) => {

    const previousData = record;
    if (!previousData) {

      return;

    }

    const base = values.location as PointGeometry | null;
    const floor = (values as SecurityInventoryItem).location?.properties?.address?.floor;

    const location = base ? {
      ...base,
      coordinates: moveMarker || !previousData.location?.coordinates
        ? base.coordinates
        : previousData.location.coordinates,
      properties: {
        ...base.properties,
        address: {
          ...base.properties?.address,
          ...(floor !== undefined && floor !== null ? { floor } : {}),
        },
      },
    } : null;

    const lastInspectionDate = new Date(values.lastInspectionDate as string).toISOString();
    const expiryDate = new Date(values.expiryDate as string).toISOString();
    const plannedRenewalDate = values.plannedRenewalDate ? new Date(values.plannedRenewalDate as string).toISOString() : null;

    const data = {
      name: values.name,
      lastInspectionDate,
      expiryDate,
      plannedRenewalDate,
      identifier: values.identifier,
      location,
    };

    await xgacDataProvider.update(securityItemsResource, {
      id: previousData._id,
      data,
      previousData,
    });
    refresh();
    props.onClose();

    notify('ra.notification.updated', {
      messageArgs: { smart_count: 1 },
      type: 'success',
    });

  };

  const handleSubmit = async (values: Record<string, unknown>) => {

    const newCoordinates = (values.location as PointGeometry | null)?.coordinates;
    const previousCoordinates = record.location?.coordinates;

    if (newCoordinates && previousCoordinates && !_.isEqual(newCoordinates, previousCoordinates)) {

      setPendingValues(values);
      setConfirmDialogOpen(true);
      return;

    }

    try {

      await performUpdate(values, false);

    } catch {

      notify('ra.notification.http_error', { type: 'warning' });

    }

  };

  return (
    // disablePortal in this case allows the location input to appear on top of the dialog and not be covered by the dialog
    // onClose is set to prevent the dialog from being closed when a click outside of the dialog occurs
    <Dialog
      open={props.open}
      onClose={(_event, reason) => {

        if (reason === 'backdropClick') {

          return;

        }
        props.onClose();

      }}
      disablePortal
      sx={{
        '& .MuiDialog-paper': {
          width: '800px',
          maxWidth: '100%',
          maxHeight: '600px',
        },
      }}>
      <DialogTitle>{translate('resources.security-inventory-items.text.edit_item_title')}</DialogTitle>
      <DialogContent>
        <RecordContextProvider value={record}>
          <Form
            id="edit-item-form"
            record={record}
            onSubmit={handleSubmit}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 3 }}>
                  <GrayTextInputWithUpperLabel
                    source="name"
                    label="general.fields.name"
                    fullWidth
                    validate={required()}
                    helperText={false}
                    sx={{ mt: 0, mb: 0 }}
                    isRequired
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <GrayTextInputWithUpperLabel
                    source="identifier"
                    label="resources.security-inventory-items.fields.identifier"
                    fullWidth
                    placeholder={translate('resources.security-inventory-items.fields.identifier_placeholder')}
                    helperText={false}
                    sx={{
                      width: '100%',
                      mt: 0,
                      mb: 0,
                    }}
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 3 }}>
                  <GrayGoogleCoordinatesInputWithUpperLabel
                    source="location"
                    isRequired
                    label="general.fields.position.address.name"
                    placeholder={translate('resources.buttons.text.building_dialog.0.input_label')}
                    validate={required()}
                    size="medium"
                    helperText={false}
                    sx={{
                      minWidth: '100%',
                      mt: 0,
                      mb: 0,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <GrayNumberInputWithUpperLabel
                    source="location.properties.address.floor"
                    isRequired
                    label={translate('general.text.map_floors')}
                    size="medium"
                    fullWidth
                    validate={required()}
                    helperText={false}
                    sx={{ mt: 0, mb: 0 }}
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
                <Box sx={{ flex: 3, minWidth: 0 }}>
                  <ExpiryDateDisplay
                    startDateSource="lastInspectionDate"
                    expiryDateSource="expiryDate"
                    timeGranularity={category?.expiryInterval}
                    startDateLabel="resources.security-inventory-items.fields.last_inspection_date"
                    expiryDateLabel="resources.security-inventory-items.fields.expiry_date"
                    helperText={translate('resources.security-inventory-items.text.expiry_date_manual_edit_helper_text')}
                    usage="inventoryItems"
                    isRequired
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <GrayDateInputWithUpperLabel
                    source="plannedRenewalDate"
                    label="resources.security-inventory-items.fields.planned_inspection_date"
                    helperText="resources.security-inventory-items.text.planned_inspection_date_helper_text"
                    size="medium"
                    fullWidth
                    inputProps={{ min: getMinimumNextDate() }}
                    FormHelperTextProps={{
                      sx: {
                        mt: 0.5,
                        marginLeft: 0,
                        fontSize: '0.625rem',
                      },
                    }}
                  />
                </Box>
              </Stack>
              <NextEmailAlert type="security-inventory" category={category} users={props.users} />
            </Stack>
          </Form>
        </RecordContextProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{translate('ra.action.cancel')}</Button>
        <Button type="submit" form="edit-item-form">{translate('ra.action.save')}</Button>
      </DialogActions>
      <Confirm
        isOpen={confirmDialogOpen}
        title={translate('resources.security-inventory-items.text.confirm_move_marker_title')}
        content={translate('resources.security-inventory-items.text.confirm_move_marker_content')}
        cancel={translate('resources.buttons.automove_no')}
        onClose={async () => {

          if (!pendingValues) return;
          try {

            await performUpdate(pendingValues, false);
            setConfirmDialogOpen(false);
            setPendingValues(null);

          } catch {

            notify('ra.notification.http_error', { type: 'warning' });

          }

        }}
        onConfirm={async () => {

          if (!pendingValues) return;
          try {

            await performUpdate(pendingValues, true);
            setConfirmDialogOpen(false);
            setPendingValues(null);

          } catch {

            notify('ra.notification.http_error', { type: 'warning' });

          }

        }}
      />
    </Dialog>
  );

};
